export interface ExerciseItem {
  id: string;
  type: string; // e.g., 'multiple-choice', 'fill-in-the-blank', 'video'
  name?: string; // Name of the exercise, might be similar to title
  title?: string; // Title of the exercise
  description?: string;
  moduleId?: string;
  content?: any; // Can be specific based on type, e.g., MultipleChoiceContent, VideoContent
  options?: ExerciseOption[]; // Using the ExerciseOption interface
  correctOption?: string | string[]; // For single or multiple correct answers, if not using isCorrect in options
  correctAnswers?: Record<string, string>; // For fill-in-the-blank type
  videoUrl?: string;
  text?: string; // For fill-in-the-blank or other text-based exercises
  textPassage?: string; // For comprehension questions
  expectedKeywords?: string[]; // For comprehension questions
  difficulty?: string; // e.g., 'easy', 'medium', 'hard'
  points?: number; // Points awarded for completion
  tags?: string[]; // Tags for categorization
  order?: number; // Order of the exercise in a sequence
  estimatedTime?: number; // Estimated time to complete in minutes
  isCompleted?: boolean; // Whether the user has completed this exercise
  score?: number; // Score obtained by the user
  createdAt: Date;
  updatedAt?: Date;
  userAttempts?: number;
  lastAttemptAt?: Date;
  answer?: {
    isCorrect?: boolean;
    data?: any;
  };

  /** Metadados de resposta por tipo (ex.: multipleResponses para multiple_choice). Apenas flags, nunca a resposta correta. */
  answerData?: { multipleResponses?: boolean; correctOptionCount?: number };

  // Essas propriedades não existem no DTO do backend e devem ser removidas em refatorações futuras
  // Elas são mantidas temporariamente para compatibilidade com os componentes existentes
  question?: string;
}

export interface ExerciseOption {
  id?: string; // id might not be present in all cases, e.g. if options are just text
  text: string;
  order?: number; // Order of the option in a list
  // Add other option-specific fields if necessary
}

export interface PronunciationWord {
  word: string;
  phonetic: string;
  audio?: string;
}

export interface ExerciseService {
  fetchExercisesByModule(moduleId: string): Promise<ExerciseItem[]>;
}

/**
 * Interfaces para verificação de exercícios
 */

export interface VerifyExerciseRequest {
  exerciseId: string;
  moduleId: string;
  answer: any; // Pode ser string, array de strings, objeto, etc. dependendo do tipo de exercício
  exerciseType: string;
}

export interface VerifyExerciseResponse {
  isCorrect: boolean;
  feedback?: string;
  expectedAnswer?: string[];
  userAnswer?: any;
}

/**
 * Interface para resposta de avaliação de exercício (estendida)
 * Inclui dados necessários para atualizar o estado local após avaliação
 */
export interface EvaluateExerciseResponse extends VerifyExerciseResponse {
  studentAnswerIsCorrect?: boolean;
  studentAnswerFeedback?: string;
  studentAnsweredAt?: string; // ISO 8601 date string
  /** True when module content is complete per progression calculator after this evaluation (from POST evaluate). */
  isModuleContentComplete?: boolean;
  /** SpeakTheSentence async evaluation */
  speechEvaluationStatus?: string;
  contentScore?: number;
  pronunciationScore?: number;
  summary?: string;
  answerId?: string;
}

/**
 * Interface para exercício básico (sem progresso do estudante)
 */
export interface Exercise {
  id: string;
  title: string;
  description?: string;
  type: string;
  data: string; // JSON com conteúdo do exercício
  order: number;
  moduleId: string;
  packageId?: string;
  /** Metadados de resposta por tipo (ex.: multipleResponses para multiple_choice). Apenas flags, nunca a resposta correta. */
  answerData?: { multipleResponses?: boolean; correctOptionCount?: number };
}

/**
 * Interface para resposta do estudante a um exercício
 */
export interface StudentExerciseAnswer {
  exerciseId: string;
  studentAnswerData: string; // JSON com a resposta
  studentAnswerIsCorrect: boolean;
  studentAnswerFeedback: string;
  studentAnsweredAt: string; // ISO 8601 date string
}

/**
 * Interface para relatório de progresso do estudante em um módulo
 */
export interface StudentModuleProgress {
  moduleId: string;
  totalExercises: number;
  answeredExercises: number;
  correctExercises: number;
  incorrectExercises: number;
  progressPercentage: number; // 0-100
  score: number; // 0-100
  averageTime?: number; // em segundos
}

/** Metadados de resposta expostos ao estudante para múltipla escolha (sem a resposta correta). */
export interface MultipleChoiceAnswerData {
  multipleResponses: boolean;
  correctOptionCount?: number;
}

/**
 * Extrai metadados de resposta do exercício para uso na UI (sem expor a resposta correta).
 * Centraliza a lógica por tipo de exercício; múltipla escolha usa answerData.multipleResponses.
 * Análogo a getExerciseContent para conteúdo; use getExerciseAnswerData para flags por tipo.
 */
export function getExerciseAnswerData<T = Record<string, unknown>>(
  item: ExerciseItem
): T {
  const type = (item.type ?? "").toLowerCase();

  if (type === "multiple_choice") {
    const raw = item.answerData as
      | { multipleResponses?: boolean; correctOptionCount?: number }
      | undefined;
    const correctOptionCount =
      typeof raw?.correctOptionCount === "number" &&
      Number.isFinite(raw.correctOptionCount) &&
      raw.correctOptionCount > 0
        ? raw.correctOptionCount
        : undefined;
    return {
      multipleResponses: raw?.multipleResponses === true,
      correctOptionCount,
    } as T;
  }

  return {} as T;
}

/**
 * Função utilitária para extrair e tipar corretamente o conteúdo de um exercício.
 * Utiliza generics para permitir a tipagem específica do conteúdo de cada tipo de exercício.
 *
 * @example
 * interface MultipleChoiceContent {
 *   question: string;
 *   options: Array<{ text: string; isCorrect: boolean }>;
 * }
 *
 * const content = getExerciseContent<MultipleChoiceContent>(exercise);
 * console.log(content.question);
 *
 * @param item O exercício do qual extrair o conteúdo
 * @returns O conteúdo tipado do exercício ou um objeto vazio em caso de erro
 */
export function getExerciseContent<T = Record<string, any>>(
  item: ExerciseItem
): T {
  try {
    const raw =
      item.content ??
      (item as ExerciseItem & { data?: unknown }).data;
    const rawData = typeof raw === "string" ? JSON.parse(raw) : raw;

    return (rawData ?? {}) as T;
  } catch (error) {
    console.error("Erro ao processar conteúdo do exercício:", error);
    return {} as T;
  }
}
